var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const u = up.util;
class Route {
    constructor() {
        this.varyHeaders = new Set();
        this.requests = [];
    }
    matchBest(newRequest) {
        let matches = this.requests.filter((cachedRequest) => this.satisfies(cachedRequest, newRequest));
        return u.last(matches);
    }
    delete(request) {
        u.remove(this.requests, request);
    }
    put(request) {
        this.requests.push(request);
    }
    updateVary(response) {
        for (let headerName of response.varyHeaderNames) {
            this.varyHeaders.add(headerName);
        }
    }
    satisfies(cachedRequest, newRequest) {
        if (cachedRequest === newRequest)
            return true;
        return u.every(this.varyHeaders, (varyHeader) => {
            let cachedValue = cachedRequest.header(varyHeader);
            let newValue = newRequest.header(varyHeader);
            if (varyHeader === 'X-Up-Target' || varyHeader === 'X-Up-Fail-Target') {
                if (!cachedValue)
                    return true;
                if (!newValue)
                    return false;
                let cachedTokens = up.fragment.splitTarget(cachedValue);
                let newTokens = up.fragment.splitTarget(newValue);
                return u.containsAll(cachedTokens, newTokens);
            }
            else {
                return cachedValue === newValue;
            }
        });
    }
}
up.Request.Cache = class Cache {
    constructor() {
        this.reset();
    }
    reset() {
        this._routes = {};
        this._requests = [];
    }
    get(request) {
        request = this._wrap(request);
        let route = this._getRoute(request);
        let cachedRequest = route.matchBest(request);
        if (cachedRequest) {
            if (this._isUsable(cachedRequest)) {
                return cachedRequest;
            }
            else {
                this._delete(request, route);
            }
        }
    }
    put(request) {
        return __awaiter(this, void 0, void 0, function* () {
            request = this._wrap(request);
            let route = this._getRoute(request);
            let { response } = request;
            if (response)
                route.updateVary(response);
            let superseded = route.requests.filter((oldRequest) => route.satisfies(request, oldRequest));
            for (let r of superseded) {
                this._delete(r);
            }
            request.cacheRoute = route;
            route.put(request);
            this._requests.push(request);
            this._limitSize();
        });
    }
    track(existingRequest_1, newRequest_1) {
        return __awaiter(this, arguments, void 0, function* (existingRequest, newRequest, options = {}) {
            var _a;
            newRequest.trackedRequest = existingRequest;
            newRequest.state = 'tracking';
            let value;
            if (existingRequest.ended && existingRequest.response) {
                value = existingRequest.response;
            }
            else {
                value = yield u.always(existingRequest);
            }
            if (value instanceof up.Response) {
                if (options.force || existingRequest.cacheRoute.satisfies(existingRequest, newRequest)) {
                    newRequest.fromCache = true;
                    value = u.variant(value, { request: newRequest });
                    newRequest.respondWith(value);
                    u.delegate(newRequest, ['expired', 'state'], () => existingRequest);
                }
                else {
                    delete newRequest.trackedRequest;
                    newRequest.state = 'new';
                    (_a = options.onIncompatible) === null || _a === void 0 ? void 0 : _a.call(options, newRequest);
                }
            }
            else {
                newRequest.state = existingRequest.state;
                newRequest._reject(value);
            }
        });
    }
    willHaveSameResponse(existingRequest, newRequest) {
        return existingRequest === newRequest || existingRequest === newRequest.trackedRequest;
    }
    evict(condition = true, testerOptions) {
        this._eachMatch(condition, testerOptions, (request) => this._delete(request));
    }
    expire(condition = true, testerOptions) {
        this._eachMatch(condition, testerOptions, (request) => request.expired = true);
    }
    reindex(request) {
        this._delete(request);
        delete request.cacheRoute;
        this.put(request);
    }
    _delete(request) {
        var _a;
        u.remove(this._requests, request);
        (_a = request.cacheRoute) === null || _a === void 0 ? void 0 : _a.delete(request);
    }
    _getRoute(request) {
        var _a, _b;
        return request.cacheRoute || ((_a = this._routes)[_b = request.description] || (_a[_b] = new Route()));
    }
    _isUsable(request) {
        return request.age < up.network.config.cacheEvictAge;
    }
    get currentSize() {
        return this._requests.length;
    }
    get _capacity() {
        return up.network.config.cacheSize;
    }
    _limitSize() {
        for (let i = 0; i < (this.currentSize - this._capacity); i++) {
            this._delete(this._requests[0]);
        }
    }
    _eachMatch(condition = true, testerOptions, fn) {
        let tester = up.Request.tester(condition, testerOptions);
        let results = u.filter(this._requests, tester);
        u.each(results, fn);
    }
    _wrap(requestOrOptions) {
        return u.wrapValue(up.Request, requestOrOptions);
    }
};
